import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface Props {
  visible: boolean;
  onDismiss?: () => void;
}

const FREE_PERKS = [
  '✅ Access all 50+ learning scenarios',
  '✅ Age-matched content for your child',
  '✅ Unlimited child profiles',
  '✅ Track practice progress',
  '✅ Community access',
  '✅ 100% free — always',
];

export default function GuestGate({ visible, onDismiss }: Props) {
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={onDismiss}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-white rounded-3xl p-6 max-w-md w-full text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="absolute top-4 right-4 text-gray-300 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            )}

            <div className="text-5xl mb-4">🦉</div>
            <h2 className="font-display text-2xl font-extrabold text-primary-800 mb-2">
              You've had a taste of KidWise
            </h2>
            <p className="text-gray-500 text-sm mb-5 leading-relaxed">
              Create your <strong>free account</strong> to unlock all scenarios
              and help your child navigate real-life situations with confidence.
            </p>

            <ul className="text-left space-y-2 mb-6">
              {FREE_PERKS.map((perk) => (
                <li key={perk} className="text-sm text-gray-700">
                  {perk}
                </li>
              ))}
            </ul>

            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-primary-600 text-white py-3.5 rounded-2xl font-bold text-base shadow-md hover:bg-primary-700 transition-colors mb-3"
            >
              Create Free Account →
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-warm-400 text-white py-3.5 rounded-2xl font-bold text-base shadow-md hover:bg-warm-500 transition-colors mb-3"
            >
              ✨ Go Premium
            </button>

            {onDismiss && (
              <button
                onClick={onDismiss}
                className="text-gray-400 text-xs hover:text-gray-600 transition-colors"
              >
                Maybe later
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
